/**
 * PORTED — RO-3-00c / #44470.
 * Origin: `excellent/app/_modes/domain-mounts.tsx`.
 *
 * The mount map for the Mode Shell. Pure UI registry — no vault reach.
 *
 * CRM (`crm.*`) and Hiring (`ats.*`) mounts are owned by RO-3-00d / RO-3-00e;
 * until those ports land their client modules, those keys resolve to a
 * `PendingMount` placeholder so the shell never imports server-tainted app/
 * trees. Work / Product / Operations pure-data adapters come from
 * `./domain-views` (this item).
 *
 * `check-domain-mounts` still scans the Next original under `excellent/app/` —
 * this client map is not that gate's input.
 */

import type { ComponentType } from "react";
import {
  ShieldCheck,
  Kanban,
  FileText,
  Wallet,
  LayoutDashboard,
  Target,
  Activity,
  Telescope,
  Boxes,
  Map as MapIcon,
  Users,
  LifeBuoy,
  Milestone,
  GitBranch,
  Building2,
  Compass,
  FolderKanban,
  MessagesSquare,
} from "@excellent/ui/icons";
import { EmptyState } from "@excellent/ui";
import {
  WorkProjectsView,
  WorkInboxView,
  WorkChatView,
  WorkObjectivesView,
  IssuesBoardView,
  WorkPlannerView,
  FinanceLedgerView,
  ProjectLinksView,
  QualityView,
  TeamDirectoryView,
  CompanyStreamView,
  CustomDomainView,
  MarketingAnalyticsView,
  MarketingSeoView,
  DeliveryPerformanceView,
  DeliveryPrdView,
} from "./domain-views";
// RO-3-10 / #44303 — wire section-hosted CRM + hiring bodies into the Mode
// Shell. The views were rewritten under RO-3-00d / RO-3-00e; leaving them as
// PendingMount meant every mode-tier CRM/hiring area rendered an empty
// placeholder even though the components were live under client/shared.
import { LeadsView } from "../crm/leads";
import { OpportunitiesView } from "../crm/opportunities";
import { SequencesView } from "../crm/sequences";
import { AccountsView } from "../crm/accounts-view";
import { ContactsView } from "../crm/contacts-view";
import { ForecastView } from "../crm/forecast-view";
import { SupportView } from "../crm/support-view";
import { RenewalView } from "../crm/renewal-view";
import {
  JobsView,
  CandidatesView,
  PipelineHubView,
  HiringSettingsView,
} from "../hiring/views";
// RO-3-06 / #44299 — AuditsHub for ops.audits domain mount
import { AuditsHub } from "../audits/hub";

/** Placeholder for mounts that still lack a client body. */
function PendingMount({ label }: { label: string }) {
  return (
    <EmptyState
      variant="inline"
      icon={<Boxes className="size-5" />}
      title={`${label} mounts next`}
      description="This domain view is scheduled under a sibling rollover item. The Mode Shell spine is ready; the view body follows."
    />
  );
}

function pending(label: string): ComponentType<any> {
  const C = () => <PendingMount label={label} />;
  C.displayName = `PendingMount(${label})`;
  return C;
}

/** Mode-shell adapters: domain mounts pass mode basePath via props when present. */
function withBasePath(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  View: ComponentType<any>,
  fallbackBase: string,
): ComponentType<any> {
  const C = (props: {
    basePath?: string;
    searchParams?: Record<string, string | string[] | undefined>;
  }) => (
    <View
      basePath={props.basePath ?? fallbackBase}
      searchParams={props.searchParams ?? {}}
    />
  );
  C.displayName = `WithBase(${(View as { displayName?: string; name?: string }).displayName ?? (View as { name?: string }).name ?? "View"})`;
  return C;
}

function withHiringCandidates(
  fallbackBase: string,
): ComponentType<any> {
  const C = (props: {
    basePath?: string;
    searchParams?: Record<string, string | string[] | undefined>;
  }) => (
    <CandidatesView
      basePath={props.basePath ?? fallbackBase}
      searchParams={props.searchParams ?? {}}
      rawRows={[]}
    />
  );
  C.displayName = "WithBase(CandidatesView)";
  return C;
}

export const DOMAIN_MOUNTS: Record<string, Record<string, ComponentType<any>>> = {
  work: {
    "work.queue": IssuesBoardView,
    "work.objectives": WorkObjectivesView,
    "work.projects": WorkProjectsView,
    "work.planner": WorkPlannerView,
    "work.inbox": WorkInboxView,
    "work.chat": WorkChatView,
    "custom.domain": CustomDomainView,
  },
  customers: {
    "crm.portfolio": withBasePath(AccountsView, "/customers"),
    "crm.pipeline": withBasePath(OpportunitiesView, "/customers"),
    "crm.leads": withBasePath(LeadsView, "/customers"),
    "crm.accounts": withBasePath(AccountsView, "/customers"),
    "crm.contacts": withBasePath(ContactsView, "/customers"),
    "crm.forecast": withBasePath(ForecastView, "/customers"),
    "crm.opportunities": withBasePath(OpportunitiesView, "/customers"),
    "crm.sequences": withBasePath(SequencesView, "/customers"),
    "crm.support": withBasePath(SupportView, "/customers"),
    "crm.renewal": withBasePath(RenewalView, "/customers"),
    "market.hub": pending("Market"),
    "marketing.analytics": MarketingAnalyticsView,
    "marketing.seo": MarketingSeoView,
    "custom.domain": CustomDomainView,
  },
  product: {
    "projects.links": ProjectLinksView,
    "product.quality": QualityView,
    "delivery.performance": DeliveryPerformanceView,
    "delivery.prd": DeliveryPrdView,
    "custom.domain": CustomDomainView,
  },
  operations: {
    "finance.ledger": FinanceLedgerView,
    "ops.audits": AuditsHub,
    "company.board": CompanyStreamView,
    "company.simulate": pending("Simulate"),
    "team.directory": TeamDirectoryView,
    "ats.pipelineHub": withBasePath(PipelineHubView, "/operations"),
    "ats.jobs": withBasePath(JobsView, "/operations"),
    "ats.candidates": withHiringCandidates("/operations"),
    // JobPipelineView requires a job slug — mode shell has no slug; hub is the right mount.
    "ats.jobPipeline": withBasePath(PipelineHubView, "/operations"),
    "ats.settings": HiringSettingsView,
    "custom.domain": CustomDomainView,
  },
  agents: {
    "custom.domain": CustomDomainView,
  },
};

export const SELF_PADDED_MOUNTS: ReadonlySet<string> = new Set([
  "work.objectives",
  "work.projects",
  "market.hub",
  "ops.audits",
]);

export function mountSelfPads(componentKey?: string | null): boolean {
  return componentKey != null && SELF_PADDED_MOUNTS.has(componentKey);
}

export const MOUNTS_WITH_OWN_VIEW_NAV: ReadonlySet<string> = new Set(["work.queue"]);

const ICONS: Record<string, ComponentType<{ className?: string }>> = {
  shieldCheck: ShieldCheck,
  kanban: Kanban,
  fileText: FileText,
  wallet: Wallet,
  layoutDashboard: LayoutDashboard,
  target: Target,
  activity: Activity,
  telescope: Telescope,
  boxes: Boxes,
  map: MapIcon,
  users: Users,
  lifeBuoy: LifeBuoy,
  milestone: Milestone,
  gitBranch: GitBranch,
  building2: Building2,
  compass: Compass,
  folderKanban: FolderKanban,
  messagesSquare: MessagesSquare,
};

export function domainIcon(key: string) {
  const I = ICONS[key] ?? Boxes;
  return <I className="size-4" />;
}
